<template>
    <div v-if="content.length === 0" class="max-w-7xl mx-auto">
        <div class="w-full m-auto justify-center items-center">
            <div class="flex">
                <fa-icon icon="smile-wink" class="mx-auto text-7xl text-gray-300"></fa-icon>
            </div>
            <h2 class="my-2 text-4xl text-gray-300 text-center">
                {{ $t("table.no_items_found") }}
            </h2>
        </div>
    </div>

    <div v-else class="flex flex-col">
        <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th
                                    v-for="header in headers"
                                    :key="header"
                                    scope="col"
                                    class="
                                        px-6
                                        py-3
                                        text-left text-xs
                                        font-medium
                                        text-gray-500
                                        uppercase
                                        tracking-wider
                                    "
                                >
                                    {{ $t(`table.${header}`) }}
                                </th>
                            </tr>
                        </thead>

                        <!-- Even row -->
                        <tbody>
                            <tr v-for="(item, itemIndex) in contentComputed" :key="item" class="bg-gray-50">
                                <td
                                    v-for="(value, index) in item"
                                    :key="value"
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                                >
                                    <div v-if="checkBoolean(value) && index === 'active_incident'" class="flex">
                                        <div
                                            v-if="value"
                                            class="bg-yellow-400 text-yellow-700 rounded-full px-4 py-1 text-sm"
                                        >
                                            {{ $t("tenants.table.status_items.active_incident") }}
                                        </div>

                                        <div v-else class="bg-green-300 text-white rounded-full px-4 py-1 text-sm">
                                            {{ $t("tenants.table.status_items.working_fine") }}
                                        </div>
                                    </div>
                                    <div v-else-if="checkBoolean(value)" class="flex">
                                        <div
                                            v-if="!value"
                                            class="bg-yellow-400 text-yellow-700 rounded-full px-4 py-1 text-sm"
                                        >
                                            {{ $t("tenants.table.status_items.no") }}
                                        </div>

                                        <div v-else class="bg-green-300 text-white rounded-full px-4 py-1 text-sm">
                                            {{ $t("tenants.table.status_items.yes") }}
                                        </div>
                                    </div>
                                    <div v-else-if="checkLink(value)" class="flex">
                                        <Link
                                            :href="value.href"
                                            :title="value.title"
                                            class="font-medium text-lightblue-700 hover:text-lightblue-500"
                                        >
                                            {{ value.title }}
                                        </Link>
                                    </div>
                                    <div v-else-if="index === 'actions'" class="flex">
                                        <span
                                            v-for="(action, actionIndex) in value"
                                            :key="actionIndex"
                                            class="font-medium mr-2 flex"
                                            :class="
                                                action.isLoading
                                                    ? 'text-lightblue-300'
                                                    : 'text-lightblue-700 hover:text-lightblue-500 cursor-pointer'
                                            "
                                            @click="
                                                action.type === 'emit' && !action.isLoading
                                                    ? $emit('action-click', { index: itemIndex, action: action.action })
                                                    : null
                                            "
                                        >
                                            <Link
                                                v-if="action.type === 'link'"
                                                :href="action.isLoading ? '#' : action.action"
                                                class="font-medium mr-2"
                                                :class="
                                                    action.isLoading
                                                        ? 'text-lightblue-300'
                                                        : 'text-lightblue-700 hover:text-lightblue-500 cursor-pointer'
                                                "
                                            >
                                                {{ action.name }}
                                            </Link>

                                            {{ action.type !== "link" ? action.name : "" }}

                                            <svg
                                                v-if="action.isLoading"
                                                class="animate-spin text-lightblue-700 ml-1 my-auto h-3 w-3"
                                                viewBox="0 0 512 512"
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="M460.116 373.846l-20.823-12.022c-5.541-3.199-7.54-10.159-4.663-15.874 30.137-59.886 28.343-131.652-5.386-189.946-33.641-58.394-94.896-95.833-161.827-99.676C261.028 55.961 256 50.751 256 44.352V20.309c0-6.904 5.808-12.337 12.703-11.982 83.556 4.306 160.163 50.864 202.11 123.677 42.063 72.696 44.079 162.316 6.031 236.832-3.14 6.148-10.75 8.461-16.728 5.01z"
                                                ></path>
                                            </svg>
                                        </span>
                                    </div>
                                    <div v-else>
                                        {{ value }}
                                    </div>
                                </td>
                            </tr>

                            <!-- More people... -->
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FaIcon from "@/Components/FaIcon";
import { computed } from "vue";
import { Link } from "@inertiajs/inertia-vue3";

export default {
    name: "Table",
    components: {
        FaIcon,
        Link,
    },
    props: {
        headers: {
            type: Array,
            required: true,
        },
        content: {
            type: Array,
            required: true,
        },
        loadingItems: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    emits: ["action-click"],
    setup(props) {
        const checkBoolean = (value) => typeof value === "boolean";
        const checkLink = (value) => typeof value === "object" && value && value.title && value.href;
        const contentComputed = computed(() => {
            const tableContent = props.content;
            if (!props.content) {
                return [];
            }

            tableContent.forEach((item, index) => {
                if (item.actions) {
                    item.actions = item.actions.map((action) => ({
                        action: action.action,
                        type: action.type,
                        name: action.name,
                        isLoading:
                            props.loadingItems.filter((e) => e.index === index && e.action === action.action).length >
                            0,
                    }));
                }
            });

            return tableContent;
        });

        return { checkBoolean, checkLink, contentComputed };
    },
};
</script>
